import { Flex } from "~/components/Flex";
import { ButtonProps, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useRiskFactorsConditionRow } from "~/pages/security-model/RiskFactorsConfiguration/ConditionBuilder/ConditionRow/hooks/useRiskFactorsConditionRow";
import { RiskFactorsConditionValues } from "~/pages/security-model/RiskFactorsConfiguration/ConditionBuilder/ConditionValues/RiskFactorsConditionValues";
import { RiskFactorsConditionTagOperator } from "~/pages/security-model/RiskFactorsConfiguration/ConditionBuilder/ConditionTagOperator/RiskFactorsConditionTagOperator";
import { ConditionTagValue } from "~/components/ConditionBuilder/ConditionTagValue/ConditionTagValue";
import { RiskFactorsConditionTagKeyAutocomplete } from "~/pages/security-model/RiskFactorsConfiguration/ConditionBuilder/ConditionTagAutocomplete/RiskFactorsConditionTagKeyAutocomplete";
import { IndentIcon } from "~/icons";

export type ConditionRowProps = {
  fieldId: string;
  selectionIndex: number;
  conditionIndex: number;
  onRemove: ButtonProps["onClick"];
  isReadonly: boolean;
};

export const RiskFactorsConditionRow = ({
  fieldId,
  conditionIndex,
  selectionIndex,
  onRemove,
  isReadonly,
}: ConditionRowProps) => {
  const {
    showConditionOperator,
    showConditionKeyLabel,
    showRemoveConditionButton,
    isKeySelected,
    isDictionaryKeySelected,
    conditionKeys,
    conditionValues,
    conditionValuesOptions,
    selectedConditionKeyLabel,
    conditionValuesFieldName,
    conditionFormDictionaryKey,
    handleConditionValueRemove,
    handleConditionKeyRemove,
    handleKeyChange,
    handleValuesItemClick,
    handleDropdownSearchFieldKeyUp,
    handleDropdownSearchFieldChange,
    isLoading,
    isValuesDropdownOpen,
    handleValuesDropdownOpen,
    handleValuesDropdownClose,
    handleKeyValueKeyClick,
    handleKeyValueValueClick,
  } = useRiskFactorsConditionRow({
    fieldId,
    selectionIndex,
    conditionIndex,
    isReadonly,
  });

  return (
    <Flex>
      <Flex gap={2} alignItems="flex-start" flexWrap="wrap">
        {showConditionOperator && (
          <Flex alignItems="center" gap={2} ml={6}>
            <IndentIcon
              sx={{ color: (theme) => theme.palette.text.disabled }}
            />
            <RiskFactorsConditionTagOperator
              fieldId={fieldId}
              selectionIndex={selectionIndex}
              conditionIndex={conditionIndex}
              isReadonly
              type="condition"
            />
          </Flex>
        )}
        {showConditionKeyLabel ? (
          <ConditionTagValue
            value={selectedConditionKeyLabel || ""}
            onClick={handleConditionKeyRemove}
            isReadonly={isReadonly}
          />
        ) : (
          conditionValues.length === 0 &&
          !isReadonly && (
            <RiskFactorsConditionTagKeyAutocomplete
              name={`${fieldId}.selections.${selectionIndex}.conditions.${conditionIndex}.formKey`}
              groups={conditionKeys}
              onChange={handleKeyChange}
            />
          )
        )}
        {isKeySelected && (
          <RiskFactorsConditionTagOperator
            fieldId={fieldId}
            selectionIndex={selectionIndex}
            conditionIndex={conditionIndex}
            isReadonly
          />
        )}
        <RiskFactorsConditionValues
          values={conditionValues}
          options={conditionValuesOptions}
          isKeySelected={isKeySelected}
          isDictionaryKeySelected={isDictionaryKeySelected}
          onValueRemove={handleConditionValueRemove}
          name={conditionValuesFieldName}
          conditionFormDictionaryKey={conditionFormDictionaryKey}
          isReadonly={isReadonly}
          isLoading={isLoading}
          onDropdownSearchFieldKeyUp={handleDropdownSearchFieldKeyUp}
          onDropdownSearchFieldChange={handleDropdownSearchFieldChange}
          onItemClick={handleValuesItemClick}
          handleKeyValueKeyClick={handleKeyValueKeyClick}
          handleKeyValueValueClick={handleKeyValueValueClick}
          isValuesDropdownOpen={isValuesDropdownOpen}
          onValuesDropdownOpen={handleValuesDropdownOpen}
          onValuesDropdownClose={handleValuesDropdownClose}
        />
      </Flex>
      {showRemoveConditionButton && (
        <Flex
          sx={{
            flex: "1 0 auto",
            justifyContent: "flex-end",
          }}
          className="delete-condition-button"
          alignItems="flex-start"
        >
          <IconButton
            onClick={onRemove}
            sx={{ color: (theme) => theme.palette.error.main }}
          >
            <Close />
          </IconButton>
        </Flex>
      )}
    </Flex>
  );
};
